import { Logger } from "neo-common";
import { Platforms, Plugins } from "neo-games";
import { StageController } from "../Components/StageController";
import { AppContext } from "../../Common/AppContext";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "./FrameTimer";
import { Jackpot } from "../Channels/Common/Bonuses/Jackpot";
import EventEmitter = PIXI.utils.EventEmitter;

/**
 * @class Scaling
 */
export class Scaling {
    static EventResize: string = "eventResize";

    static Root: HTMLElement;
    static Screen: HTMLElement;
    static Canvas: HTMLElement;
    static lastTouch: number;

    static PortraitRatio = 0.9;
    static PortraitMobileRatio = 0.72;

    static LeftPanelWidth = 0;

    static IsIOs: boolean = false;
    static IsIOsTablet: boolean = false;
    static IsIOsWebview: boolean = false;

    static IOsResizeTimer: FrameTimerEventEmitter;
    static IOsResizeDelay = 60;

    static Emitter: EventEmitter;

    static scale(): void {
        Scaling.Root = document.getElementById("root");
        Scaling.Screen = document.getElementById("screen");
        Scaling.IsIOs = /iPad|iPhone|iPod|MacIntel/.test(navigator.platform);
        Scaling.IsIOsTablet = /iPad/.test(navigator.platform);
        Scaling.IsIOsWebview = Scaling.IsIOs && window.indexedDB !== null;
        Scaling.IOsResizeTimer = CreateFrameTimer("scalingTimer");
        Scaling.IOsResizeTimer.on(FrameTimerEvents.Complete, Scaling.resizeRoot.bind(this, true));

        Scaling.Emitter = new EventEmitter();

        Scaling.resizeRoot();
        window.onresize = Scaling.onResizeRoot;

        document.body.classList.add(window["CHANNEL"] === "mobile" ? "mobile" : "desktop");
        document.body.classList.add(`ui-v${window["UI-VERSION"]}`);

        if (Scaling.isSafari()) {
            document.body.classList.add("safari");
        }

        if (Scaling.isIosChrome()) {
            document.body.classList.add("iOsChrome");
        }

        if (Scaling.IsIOs) {
            Scaling.setupIOs();
        }

        /**
         * Fast Touchstart events replaced by click event.
         */
        document.addEventListener(
            "touchend",
            function (event) {
                if (AppContext.gameDefinition.ClientHostType !== "Mobile") {
                    return;
                }

                let diff = event.timeStamp - Scaling.lastTouch;
                Scaling.lastTouch = event.timeStamp;
                if (!diff || diff > 400) {
                    return;
                }
                event.preventDefault();
                event.stopPropagation();

                let evt = new MouseEvent("click");
                event.target.dispatchEvent(evt);
            },
            false,
        );
    }

    static onResizeRoot(): void {
        Scaling.resizeRoot();
        if (Scaling.IsIOsWebview) {
            setTimeout(() => Scaling.resizeRoot(), 500);
        }
    }

    static resizeRoot(delayed: boolean = false, preventEmitEvent: boolean = true): void {
        if (!delayed && Scaling.IsIOs && Scaling.IOsResizeTimer && (Scaling.inIframe() || Scaling.isIosChrome())) {
            Scaling.IOsResizeTimer.stop();
            Scaling.IOsResizeTimer.start(Scaling.IOsResizeDelay);
        }

        document.body.style.overflow = "hidden"; //calculate clientHeight without scrollbars
        Plugins.StatusBar.updateHeight();

        var sab = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--sab") || "0"); //safe area bottom
        var innerHeight: number = window.innerHeight + Plugins.StatusBar.StatusBarHeight - sab;
        var innerWidth: number = window.innerWidth + Plugins.StatusBar.StatusBarWidth;

        if (Scaling.IsIOs && Plugins.StatusBar.StatusBarHeight != 0) {
            //to prevent StatusBar from being positioned below game
            document.documentElement.style.width = sab > 0 ? window.innerWidth + "px" : "";
            document.documentElement.style.height = sab > 0 ? window.innerHeight - sab + "px" : "";
            document.documentElement.style.overflow = sab > 0 ? "visible" : "";
        }

        var ratioWidth: number = innerWidth / (StageController.Width + Scaling.LeftPanelWidth);
        var ratioHeight: number = innerHeight / (StageController.Height + Jackpot.Height);
        var top: number = -(StageController.Height + Jackpot.Height - innerHeight) / 2;
        var left: number = -(StageController.Width - innerWidth) / 2;

        if (ratioWidth < ratioHeight) {
            Scaling.Root.style.transform = "scale(" + ratioWidth + ")";
            left += (Scaling.LeftPanelWidth / 2) * ratioWidth;
        } else {
            Scaling.Root.style.transform = "scale(" + ratioHeight + ")";
            left += (Scaling.LeftPanelWidth / 2) * ratioHeight;
        }

        Scaling.Root.style.top = top.toString() + "px";
        Scaling.Root.style.left = left.toString() + "px";

        let enabledPortrait = true;
        if (AppContext != null && AppContext.current != null) {
            enabledPortrait = AppContext.gameConfiguration.UsePortraitMode;

            //disable portrait for ipads
            if (Scaling.IsIOsTablet) {
                enabledPortrait = false;
            }

            if (!enabledPortrait) {
                document.body.classList.remove("portrait", "mobile-xs", "tablet", "tablet-xs");
                Scaling.Root.style.height = StageController.Height + Jackpot.Height + "px";
            }
        }

        if (enabledPortrait && window["CHANNEL"] === "mobile") {
            let portraitRatio = innerWidth / innerHeight;
            if (innerHeight > innerWidth && portraitRatio < Scaling.PortraitRatio) {
                document.body.classList.add("portrait");
                document.body.classList.remove("mobile-xs", "tablet", "tablet-xs");

                if (portraitRatio > Scaling.PortraitMobileRatio && portraitRatio < Scaling.PortraitRatio) {
                    document.body.classList.add("tablet");

                    if (portraitRatio > 0.85) {
                        document.body.classList.add("tablet-xs");
                    }
                } else {
                    if (portraitRatio > 0.57) {
                        document.body.classList.add("mobile-xs");
                    }
                }

                ratioWidth = innerWidth / StageController.Height;
                left = -(StageController.Height - innerWidth) / 2;

                Scaling.Root.style.transform = "scale(" + ratioWidth + ")";
                Scaling.Root.style.top = "0";
                Scaling.Root.style.left = left.toString() + "px";
                Scaling.Root.style.height = (StageController.Height / innerWidth) * innerHeight + "px";
            } else {
                document.body.classList.remove("portrait", "tablet");
                Scaling.Root.style.height = StageController.Height + Jackpot.Height + "px";
            }

            if (document.body.classList.contains("portrait")) {
                Scaling.Root.classList.remove("gameShadow");
                Scaling.Screen.classList.add("gameShadow");
            } else {
                Scaling.Root.classList.add("gameShadow");
                Scaling.Screen.classList.remove("gameShadow");
            }
        } else {
            Scaling.Root.style.height = StageController.Height + Jackpot.Height + "px";
            Scaling.Root.classList.add("gameShadow");
        }

        if (document.documentElement.scrollTop != 0) {
            document.documentElement.scrollTop = 0;
        }

        document.body.style.overflow = "";

        if (Scaling.Emitter && preventEmitEvent) {
            Scaling.Emitter.emit(
                Scaling.EventResize,
                Scaling.isPortrait(),
                innerWidth / (innerHeight - Jackpot.Height),
                innerWidth,
                innerHeight,
                ratioWidth,
                ratioHeight,
            );
        }
    }

    static setupIOs(): void {
        document.addEventListener("gesturestart", this.preventPropagation, true);
        document.addEventListener("gesturechange", this.preventPropagation, true);
        document.addEventListener("gestureend", this.preventPropagation, true);
        document.addEventListener("touchmove", this.preventMovePropagation, true);
    }

    public static preventPropagation(e: Event): void {
        e.stopPropagation();
        e.preventDefault();
    }

    public static preventMovePropagation(e: Event): void {
        var target = <HTMLElement>e.target;
        while (target) {
            if (target == document.documentElement) {
                target = null;
            } else if (target.classList.contains("mask") || target.classList.contains("dialogContent")) {
                return;
            } else {
                if (target.parentElement != null) {
                    target = target.parentElement;
                }
            }
        }

        e.stopPropagation();
        e.preventDefault();
    }

    private static isSafari(): boolean {
        const ua = navigator.userAgent;
        const vendor = navigator.vendor;

        return /Safari/i.test(ua) && /Apple Computer/.test(vendor);
    }

    private static isIosChrome(): boolean {
        return !!(Scaling.IsIOs && (navigator.userAgent.match("CriOS") || navigator.userAgent.match("FxiOS")));
    }

    private static inIframe(): boolean {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    }

    static onLoadComplete(): void {
        document.body.classList.add("loaded");
    }

    public static isPortrait(): boolean {
        return document.body.classList.contains("portrait");
    }

    /**
     * Trace to browser console.
     * @method log
     * @param s string which will be traced to browser console.
     * @return void
     */
    static log(s: string): void {
        if (DEBUG) {
            Logger.debugGroup("Scaling", s);
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Utils/Scaling.ts
 **/